import React, { useRef, useState, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Paper,
    IconButton,
    Slider,
    Tooltip,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    VolumeUp,
    VolumeOff,
    Fullscreen,
    Speed,
} from '@mui/icons-material';
import { intl } from 'dmsbasereact';
import './DmsVideoPlayer.css';

const SPEED_OPTIONS = [1.25, 1.5, 2];

export const DmsVideoPlayer = forwardRef(({ videoUrl, onDurationChange, children, isTraining = false }, ref) => {
    const videoRef = useRef(null);
    const playerElementRef = useRef(null);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [maxWatchedTime, setMaxWatchedTime] = useState(0);
    const [playbackRate, setPlaybackRateState] = useState(1);
    const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);

    useEffect(() => {
        const handleBlur = () => {
            if (isTraining) {
                videoRef.current?.pause();
            }
        };

        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('blur', handleBlur);
        };
    }, [isTraining]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isTraining && !entry.isIntersecting) {
                    videoRef.current?.pause();
                }
            },
            { threshold: 0.5 } // Trigger when 50% of the player is visible/hidden
        );

        const playerElement = playerElementRef.current;
        if (playerElement) {
            observer.observe(playerElement);
        }

        return () => {
            if (playerElement) {
                observer.unobserve(playerElement);
            }
        };
    }, [isTraining]);

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            const videoDuration = videoRef.current.duration;
            setDuration(videoDuration);
            if (onDurationChange) {
                onDurationChange(videoDuration);
            }
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const totalDuration = videoRef.current.duration;
            setCurrentTime(current);
            setMaxWatchedTime(prev => Math.max(prev, current));
            if (totalDuration > 0) {
                setProgress((current / totalDuration) * 100);
            }
        }
    };

    const handleEnded = () => {
        if (videoRef.current) {
            const totalDuration = videoRef.current.duration;
            setCurrentTime(totalDuration);
            setMaxWatchedTime(prev => Math.max(prev, totalDuration));
            setProgress(100);
        }
    };
    
    const formatTime = (timeInSeconds) => {
        if (isNaN(timeInSeconds) || timeInSeconds === 0) return "00:00";
        const date = new Date(0);
        date.setSeconds(timeInSeconds);
        return date.toISOString().substr(timeInSeconds >= 3600 ? 11 : 14, timeInSeconds >= 3600 ? 8 : 5);
    };

    const videoState = {
        duration,
        currentTime,
        progress,
        formatTime,
    };

    const controls = {
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        togglePlayPause: () => {
            if (videoRef.current) {
                videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause();
            }
        },
        seek: (time) => {
            if (videoRef.current && (!isTraining || time <= maxWatchedTime)) {
                videoRef.current.currentTime = time;
            }
        },
        setVolumeLevel: (level) => {
            if(videoRef.current) {
                videoRef.current.volume = level;
                setVolume(level);
            }
        },
        setPlaybackRate: (rate) => {
            if (videoRef.current) {
                videoRef.current.playbackRate = rate;
                setPlaybackRateState(rate);
            }
        },
        videoElement: videoRef.current,
        toggleFullscreen: () => {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else if (videoRef.current) {
                videoRef.current.requestFullscreen().catch(err => {
                    console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
                });
            }
        }
    };

    const setPlayerRef = (node) => {
        playerElementRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    return (
        <Box ref={setPlayerRef} className="dms-video-player" sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            p: { xs: 1, md: 2 }
        }}>
            <Paper
                elevation={24}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: { xs: 3, md: 4 },
                    overflow: 'hidden',
                    minHeight: 0,
                    maxHeight: '100%',
                    background: '#000',
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.25)',
                    transition: 'all 0.3s ease-in-out'
                }}
            >
                <>
                    {/* Video */}
                    <Box 
                        sx={{ 
                            flex: 1,
                            backgroundColor: '#000',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 0,
                            maxHeight: 'calc(100vh - 250px)'
                        }}
                    >
                        <video
                            ref={videoRef}
                            style={{ 
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                            controls={false}
                            onLoadedMetadata={handleLoadedMetadata}
                            onTimeUpdate={handleTimeUpdate}
                            onEnded={handleEnded}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            src={videoUrl}
                            playsInline
                            preload="metadata"
                            controlsList="nodownload nofullscreen noremoteplayback"
                            disablePictureInPicture
                        >
                            <source src={videoUrl} type="video/mp4" />
                            {intl.get("BrowserDoesNotSupportVideo").d("Your browser does not support the video tag.")}
                        </video>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            p: 1.5,
                            background: 'rgba(0, 0, 0, 0.6)',
                            flexShrink: 0,
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title={isPlaying ? intl.get("Tooltip.Action.Pause").d("Pause") : intl.get("Tooltip.Action.Play").d("Play")}>
                                <IconButton onClick={controls.togglePlayPause} sx={{ color: 'white' }}>
                                    {isPlaying ? <Pause /> : <PlayArrow />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={volume === 0 ? intl.get("Tooltip.Action.Unmute").d("Unmute") : intl.get("Tooltip.Action.Mute").d("Mute")}>
                                <IconButton onClick={() => controls.setVolumeLevel(volume > 0 ? 0 : 1)} sx={{ color: 'white' }}>
                                   {volume === 0 ? <VolumeOff /> : <VolumeUp />}
                                </IconButton>
                            </Tooltip>
                            <Slider
                                value={volume * 100}
                                onChange={(e, newValue) => controls.setVolumeLevel(newValue / 100)}
                                aria-labelledby="volume-slider"
                                min={0}
                                max={100}
                                sx={{ width: 100, color: 'white' }}
                            />
                        </Box>
                        
                        <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title={intl.get("Tooltip.Action.PlaybackSpeed").d("Playback Speed")}>
                                <IconButton
                                    onClick={(e) => setSpeedMenuAnchor(e.currentTarget)}
                                    sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                                >
                                    <Speed sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                                    {playbackRate}x
                                </IconButton>
                            </Tooltip>
                            <Menu
                                anchorEl={speedMenuAnchor}
                                open={Boolean(speedMenuAnchor)}
                                onClose={() => setSpeedMenuAnchor(null)}
                                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            >
                                {SPEED_OPTIONS.map((rate) => (
                                    <MenuItem
                                        key={rate}
                                        selected={rate === playbackRate}
                                        onClick={() => {
                                            controls.setPlaybackRate(rate);
                                            setSpeedMenuAnchor(null);
                                        }}
                                    >
                                        {rate}x
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Tooltip title={intl.get("Tooltip.Action.FullScreen").d("Fullscreen")}>
                                <IconButton onClick={controls.toggleFullscreen} sx={{ color: 'white' }}>
                                    <Fullscreen />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {children && (
                        <Box sx={{
                            flexShrink: 0,
                            p: 2,
                            backgroundColor: 'white',
                            borderTop: '1px solid #e0e0e0'
                        }}>
                            {children(videoState, controls)}
                        </Box>
                    )}

                    {/* Progress bar */}
                    <Slider
                        value={currentTime}
                        max={duration || 0}
                        onChange={(e, newValue) => controls.seek(newValue)}
                        aria-labelledby="progress-slider"
                        sx={{
                            height: '4px',
                            flexShrink: 0,
                            padding: '0 !important',
                            borderRadius: 0,
                            color: 'white',
                            mt: '-10px',
                            '& .MuiSlider-rail': {
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                opacity: 1,
                            },
                            '& .MuiSlider-track': {
                                background: 'linear-gradient(90deg, #00ff88 0%, #00d4ff 100%)',
                                border: 'none',
                            },
                            '& .MuiSlider-thumb': {
                                width: 4,
                                height: 4,
                                backgroundColor: '#fff',
                                '&:before': {
                                    display: 'none',
                                },
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow: 'none',
                                },
                                '&.Mui-active': {
                                    boxShadow: 'none',
                                },
                            },
                        }}
                    />
                </>
                </Paper>
        </Box>
    );
});

DmsVideoPlayer.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    onDurationChange: PropTypes.func,
    children: PropTypes.func,
    isTraining: PropTypes.bool
};